import axios, { AxiosResponse } from 'axios'
import { ID, Response } from '../../../../_metronic/helpers'
import { CityModel, SiteModel } from '../../procurement-management/core/_models'
import { FileType, FileTypeQueryResponse, FileModel } from './_models'
import { useState } from 'react'
import { toast } from "react-toastify";
import {
  getUploadFailureMessage,
  registerUploadFailureNotification,
  uploadFormDataInChunks,
} from './uploadIntegrity'

const API_URL = process.env.REACT_APP_THEME_API_URL
const USER_URL = `https://shifatr.org/api/user`
const GET_USERS_URL = `https://shifatr.org/api/users/query`
const GET_FILETYPES_URL = "https://shifatr.org/api/file"
const CHUNK_SIZE = 5 * 1024 * 1024;



const getFileTypes = (query: string): Promise<FileTypeQueryResponse> => {
  var x = axios
    .get(`${GET_FILETYPES_URL}?${query}`)
    .then((response: AxiosResponse<FileTypeQueryResponse>) => response.data)

  return x;
}

const getLots = (query: string): Promise<Array<string>> => {
  return axios
    .get(`${GET_USERS_URL}?${query}`)
    .then((response: AxiosResponse<Array<string>>) => response.data)
}

const getSites = (query: string): Promise<Array<SiteModel>> => {
  return axios
    .get(`${GET_USERS_URL}?${query}`)
    .then((response: AxiosResponse<Array<SiteModel>>) => response.data)
}

const createFileUpload = (file: FormData): Promise<Response<ID> | undefined> => {
  return uploadFormDataInChunks({
    completeUploadRequest: uploadComplete,
    fileFieldName: 'uploadedFile',
    formData: file,
    rollbackChunksRequest: rollbackChunks,
    uploadChunksRequest: uploadChunks,
    }).then((response: any) => ({
      ...response,
      data: response?.data?.value ?? response?.data,
    }))
}

const createFileUploadDirect = (file: FormData): Promise<any> => {
  return axios
    .post(`${GET_FILETYPES_URL}`, file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => response.data)
}

const createChunkFileUpload = async (file: any) => {
  return axios.post(`${GET_FILETYPES_URL}/chunkUpload`, file)
    .then((response) => response)
};

const uploadChunks = (
  chunk: Blob,
  counter: number,
  fileName: string,
  options?: { expectedChunkSize?: number; uploadId?: string }
) => {
  const formData = new FormData();
  formData.append("chunk", chunk);
  formData.append("chunkNumber", counter.toString());
  formData.append("fileName", fileName);
  if (options?.expectedChunkSize !== undefined) {
    formData.append("expectedChunkSize", options.expectedChunkSize.toString());
  }
  if (options?.uploadId) {
    formData.append("uploadId", options.uploadId);
  }

  return axios.post(`${GET_FILETYPES_URL}/uploadChunks`, formData)
    .then((response) => response)
    .catch((error) => {
      registerUploadFailureNotification(getUploadFailureMessage(error));
      throw error;
    });
}

const rollbackChunks = (counter: number, fileName: string, uploadId?: string) => {
  const formData = new FormData();
  formData.append("id", counter.toString());
  formData.append("fileName", fileName);
  if (uploadId) {
    formData.append("uploadId", uploadId);
  }

  return axios.post(`${GET_FILETYPES_URL}/rollbackChunks`, formData)
    .then((response) => response)
    .catch((error) => {
      throw error;
    });
}

const uploadComplete = (file: FormData) => {
  return axios.post(`${GET_FILETYPES_URL}/uploadComplete`, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then((response) => {
    if (response?.data?.isSuccess === false) {
      registerUploadFailureNotification(response?.data?.error);
    }

    return response;
  }).catch((error) => {
    registerUploadFailureNotification(getUploadFailureMessage(error));
    throw error;
  });
}



const getCities = (): Promise<Array<CityModel>> => {
  return axios
    .get(`${GET_USERS_URL}`)
    .then((response: AxiosResponse<Array<CityModel>>) => response.data)
}

const downloadFileById = (id: any, fileName: any, intlnss?: any) => {
  let toastId: any = null;
  if (intlnss) {
    toastId = toast.info(intlnss.formatMessage({ id: 'DOWNLOAD_STARTED' }), {
      autoClose: false,
    });
  }

  return axios({
    responseType: "blob",
    method: "GET",
    url: `${GET_FILETYPES_URL}/${id}`,
  }).then((response) => {
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const contentType = blob.type || '';

    if (!blob.size || contentType.includes('application/json') || contentType.startsWith('text/')) {
      throw new Error('Unexpected download payload');
    }

    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}`);
      document.body.appendChild(link);
      link.click();
    }

  }).catch((error) => {
    const downloadFailedMessage = intlnss ? intlnss.formatMessage({ id: 'DOWNLOAD_FAILED' }) : 'Download failed';
    toast.error(downloadFailedMessage, {
      autoClose: false,
    });
  })
    .finally(() => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    });
}
const deleteFileById = (fileId: ID): Promise<void> => {
  return axios.delete(`${GET_FILETYPES_URL}/file-control/${fileId}`).then(() => { })
}

const getProgressPaymentFileInfoById = (id: ID): Promise<any> => {
  return axios
    .get(`${GET_FILETYPES_URL}/procurement/info/progress-payment-file/${id}`, {
      timeout: 8000,
    })
    .then((response: AxiosResponse<any>) => response.data ? response.data : null)
}

const updateFileUploadDate = (progressPaymentId: ID, documentDate: string): Promise<any> => {
  return axios.put(`${GET_FILETYPES_URL}/update-file-date/${progressPaymentId}`, JSON.stringify(documentDate), {
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export {
  createFileUpload,
  getFileTypes,
  getLots,
  getCities,
  getSites,
  downloadFileById,
  deleteFileById,
  getProgressPaymentFileInfoById,
  createChunkFileUpload as createChunkFile,
  createFileUploadDirect,
  updateFileUploadDate,
  uploadChunks,
  rollbackChunks,
  uploadComplete,
}
